import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Divider, Radio, Select, Spin } from "antd";

import ApiMcp from "../../api/mcp";
import ApiProjects from "../../api/projects";
import {
  SESSION_ANONYMOUS,
  SESSION_UNKNOWN,
  selectSessionStatus,
  selectUser,
} from "../../store/slices/user";
import { clearReturnTo, rememberReturnTo } from "../../common/utils/return-to";
import "./styles.css";

const LOGO_SRC = `${
  process.env.REACT_APP_DEV_URL || document.location.origin
}/api/images/logo.svg`;

// Projects still being set up or torn down cannot take a connection.
const UNAVAILABLE = ["provisioning", "canceling"];

const GONE = ["MCP_REQUEST_NOT_FOUND", "MCP_REQUEST_EXPIRED", "MCP_REQUEST_DECIDED"];

/**
 * LF360-2100 — where an AI assistant sends the user to allow it into LF360.
 *
 * Claude or ChatGPT starts the connection and opens this page with the id of
 * its request. It is part of the portal on purpose: a user who is signed in
 * just sees "Allow", one who is not signs in the usual way and comes back here
 * — and nothing on it signs anybody in again, which would end their other
 * portal session.
 *
 * Public in the router (outside PrivateRoutes and the project layout) because
 * it decides about the session itself: it has to remember this address before
 * sending the user to sign in, and no project is open yet — choosing one is
 * what the page is for.
 *
 * What is shown about the assistant is what identifies it: its name, which it
 * chose itself, and the domain the answer goes back to, which it cannot fake.
 */
function OAuthConsent() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("request");
  const sessionStatus = useSelector(selectSessionStatus);
  const user = useSelector(selectUser);

  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState(null);
  const [details, setDetails] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [scopes, setScopes] = useState([]);
  const [expiresInDays, setExpiresInDays] = useState(null);
  const [sending, setSending] = useState(false);

  const signedIn = sessionStatus !== SESSION_UNKNOWN && sessionStatus !== SESSION_ANONYMOUS;

  useEffect(() => {
    if (!signedIn || !requestId) return undefined;

    clearReturnTo();

    let alive = true;
    Promise.all([ApiMcp.getAuthorization(requestId), ApiProjects.getMine()])
      .then(([authorization, mine]) => {
        if (!alive) return;

        const data = authorization.data;
        setDetails(data);
        setExpiresInDays(data.defaultExpiryDays);
        // Only the projects the server says the user may connect to: those
        // that allow AI connectors, where their role has "Use AI connectors".
        const connectable = new Set((data.projects || []).map((entry) => entry.projectId));
        setProjects(
          (mine.data.projects || []).filter(
            (p) => !UNAVAILABLE.includes(p.provisionStatus) && connectable.has(p.id)
          )
        );
        if (!data.canConnect) setProblem("not_allowed");
      })
      .catch((error) => {
        if (!alive) return;
        setProblem(GONE.includes(error?.response?.data?.error) ? "expired" : "failed");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [signedIn, requestId]);

  const projectOptions = useMemo(
    () => projects.map((project) => ({ value: project.id, label: project.title })),
    [projects]
  );

  // The chosen project's entry: the scopes the user may give a connection
  // there, and why each other one is not on offer ('role' or 'policy').
  const projectEntry = useMemo(
    () => (details?.projects || []).find((item) => item.projectId === projectId),
    [details, projectId]
  );
  const allowedScopes = useMemo(() => (projectEntry ? projectEntry.scopes : []), [projectEntry]);

  // Read of every type the project offers, ticked; Draft is chosen, never a default.
  const chooseProject = (id) => {
    setProjectId(id);
    const entry = (details?.projects || []).find((item) => item.projectId === id);
    setScopes(entry ? entry.scopes.filter((key) => key.endsWith(":read")) : []);
  };

  // Draft brings its type's Read with it; Read taken away takes Draft too.
  const toggleScope = (scope, checked) => setScopes((current) => {
    const read = `${scope.type}:read`;
    const draft = `${scope.type}:draft`;
    const next = new Set(current);
    if (checked) {
      next.add(scope.key);
      if (scope.access === "draft") next.add(read);
    } else {
      next.delete(scope.key);
      if (scope.access === "read") next.delete(draft);
    }
    return details.scopes.map((item) => item.key).filter((key) => next.has(key));
  });

  const scopeTypes = useMemo(() => {
    const types = [];
    for (const scope of details?.scopes || []) {
      if (!types.some((entry) => entry.type === scope.type)) types.push({ type: scope.type, label: scope.label });
    }
    return types;
  }, [details]);

  const reasonOf = (key) => {
    if (!projectId || allowedScopes.includes(key)) return null;
    return projectEntry?.unavailable?.[key] === "policy" ? "scope_not_in_project" : "scope_not_in_role";
  };

  if (sessionStatus === SESSION_UNKNOWN) {
    return <Spin fullscreen />;
  }

  if (sessionStatus === SESSION_ANONYMOUS) {
    rememberReturnTo(`${location.pathname}${location.search}`);
    return <Navigate to="/login" replace />;
  }

  // Leaving is a full navigation, to the assistant's own address.
  const goBack = (response) => window.location.assign(response.data.redirectUrl);

  const decide = (call) => {
    setSending(true);
    call()
      .then(goBack)
      .catch((error) => {
        setSending(false);
        setProblem(GONE.includes(error?.response?.data?.error) ? "expired" : "failed");
      });
  };

  const allow = () => decide(() => ApiMcp.approve(requestId, { projectId, scopes, expiresInDays }));
  const cancel = () => decide(() => ApiMcp.deny(requestId));

  const client = details?.request?.client;
  const clientName = client?.name || t("oauth_consent.unknown_client");
  const canAllow = Boolean(projectId) && scopes.length > 0
    && scopes.every((key) => allowedScopes.includes(key)) && !sending;

  const body = () => {
    if (!requestId) return <p className="oauth-consent__text">{t("oauth_consent.no_request")}</p>;
    if (loading) return <p className="oauth-consent__text">{t("oauth_consent.loading")}</p>;
    if (problem === "expired" || problem === "failed" || !details) {
      return <p className="oauth-consent__text" role="alert">{t(`oauth_consent.${problem || "failed"}`)}</p>;
    }

    return (
      <>
        <h1 className="oauth-consent__title">{t("oauth_consent.requests_access", { client: clientName })}</h1>
        <dl className="oauth-consent__client">
          <dt>{t("oauth_consent.client_domain")}</dt>
          <dd className="oauth-consent__domain">{client.host}</dd>
          <dt>{t("oauth_consent.returns_to")}</dt>
          <dd className="oauth-consent__redirect">{client.redirectUri}</dd>
        </dl>
        {user?.email && (
          <p className="oauth-consent__muted">{t("oauth_consent.signed_in_as", { email: user.email })}</p>
        )}

        {problem === "not_allowed" ? (
          <p className="oauth-consent__text" role="alert">{t("oauth_consent.not_allowed")}</p>
        ) : (
          <>
            <label className="oauth-consent__label" htmlFor="oauth-consent-project">
              {t("oauth_consent.project")}
            </label>
            <Select
              id="oauth-consent-project"
              data-testid="oauth-consent-project"
              className="oauth-consent__select"
              placeholder={t("oauth_consent.project_placeholder")}
              options={projectOptions}
              value={projectId}
              onChange={chooseProject}
              showSearch
              optionFilterProp="label"
            />

            <p className="oauth-consent__label">{t("oauth_consent.scopes")}</p>
            <div className="oauth-consent__scopes" role="group" aria-label={t("oauth_consent.scopes")}>
              {scopeTypes.map(({ type, label }) => (
                <div className="oauth-consent__scope-row" key={type}>
                  <span className="oauth-consent__scope-type">{label}</span>
                  {["read", "draft"].map((access) => {
                    const scope = details.scopes.find((item) => item.key === `${type}:${access}`);
                    if (!scope) return null;
                    const reason = reasonOf(scope.key);
                    const accessLabel = t(`oauth_consent.scope_${access}`);
                    return (
                      <span className="oauth-consent__scope" key={scope.key}>
                        <Checkbox
                          aria-label={`${label}: ${accessLabel}`}
                          checked={scopes.includes(scope.key)}
                          disabled={!projectId || Boolean(reason)}
                          onChange={(event) => toggleScope(scope, event.target.checked)}
                        >
                          {accessLabel}
                        </Checkbox>
                        {reason && <span className="oauth-consent__muted">{t(`oauth_consent.${reason}`)}</span>}
                      </span>
                    );
                  })}
                </div>
              ))}
            </div>

            <p className="oauth-consent__label">{t("oauth_consent.expiry")}</p>
            <Radio.Group
              value={expiresInDays}
              onChange={(event) => setExpiresInDays(event.target.value)}
              options={details.expiryDays.map((days) => ({
                value: days,
                label: t("oauth_consent.days", { count: days }),
              }))}
            />

            <p className="oauth-consent__muted">{t("oauth_consent.read_only_note")}</p>
          </>
        )}

        <div className="oauth-consent__actions">
          <Button onClick={cancel} disabled={sending}>{t("oauth_consent.cancel")}</Button>
          {problem !== "not_allowed" && (
            <Button type="primary" onClick={allow} disabled={!canAllow} loading={sending}>
              {t("oauth_consent.allow")}
            </Button>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="oauth-consent">
      <div className="oauth-consent__container">
        <div className="oauth-consent__header">
          <img className="oauth-consent__logo" src={LOGO_SRC} alt="LifeFit360" />
          <p className="oauth-consent__muted">{t("oauth_consent.title")}</p>
        </div>
        <Divider />
        <div className="oauth-consent__body">{body()}</div>
      </div>
    </div>
  );
}

export default OAuthConsent;
